// The scored risk questions of the client application (SEP-293.1).
//
// Form version 3.0 asks the risk questions of the client with the words of the
// "Existing Clients" v6 fact find (SEP-288). The risk instrument of the fact
// find scores twelve questions, and it scores the CANONICAL option label of
// `apps/web/src/lib/atr-scoring.ts`.
//
// # Four questions get a new id
//
// Four risk questions of version 2.0 change their meaning or their options in
// version 3.0. Each one gets a new id, and version 3.0 retires the old id, so
// that a stored answer always means what its label says:
//
//   - `atr_q4` asks about comfort through the volatility of smaller companies,
//     and no more about an understanding of their risks;
//   - `atr_q6` asks about comfort below the original investment, and no more
//     about the loss that prompts a move to cash;
//   - `atr_q7` and `atr_q12` change their bands.
//
// # The score does not change
//
// The application stores the words that the client read. The prefill map
// (`client-application-fact-find-map.ts`) turns each one into the canonical
// label at the SAME POSITION, with the map of SEP-288
// (`EXISTING_CLIENT_ATR_WORDING`). Compliance must confirm that map (SEP-288,
// SEP-293 question Q6).
//
// A submission of version 2.0 still supplies a fact find: an upgrade step
// moves each retired answer to the new id at the same position. A draft does
// not get that step, because the client must read the new question and answer
// it again.

import { atrOptionWording, EXISTING_CLIENT_ATR_WORDING } from './fact-find-existing-client';

/** One risk question that form version 3.0 asks under a new id. */
export interface ClientApplicationRiskQuestionChange {
    /** The id of form version 3.0. */
    readonly questionId: string;
    /** The id of form version 2.0, which version 3.0 retires. */
    readonly retiredQuestionId: string;
    /** The question of the risk instrument of the fact find. */
    readonly atrQuestionId: string;
    /**
     * The canonical option labels of the instrument, in its order. Null when
     * the application keeps its own option labels (the agreement scale).
     * `apps/web/src/lib/client-application-scored-risk.test.ts` holds each
     * list equal to `atr-scoring.ts`.
     */
    readonly canonicalOptions: readonly string[] | null;
}

/** The four risk questions that form version 3.0 asks under a new id. */
export const CLIENT_APPLICATION_RISK_QUESTION_CHANGES: readonly ClientApplicationRiskQuestionChange[] =
    [
        {
            questionId: 'risk_comfortable_through_smaller_company_volatility',
            retiredQuestionId: 'risk_understands_smaller_company_risks',
            atrQuestionId: 'atr_q4',
            canonicalOptions: null,
        },
        {
            questionId: 'risk_comfort_below_original_investment',
            retiredQuestionId: 'risk_loss_that_prompts_move_to_cash',
            atrQuestionId: 'atr_q6',
            canonicalOptions: [
                'Any fall would be cause for concern',
                'Loss of 15%',
                'Loss of 16-30%',
                'Loss of 31-49%',
                'Loss of 50%+',
            ],
        },
        {
            questionId: 'risk_time_horizon',
            retiredQuestionId: 'risk_investment_horizon',
            atrQuestionId: 'atr_q7',
            canonicalOptions: ['0-1 years', '1-3 years', '3-5 years', '5-7 years', '7+ years'],
        },
        {
            questionId: 'risk_bearable_loss',
            retiredQuestionId: 'risk_capacity_for_loss',
            atrQuestionId: 'atr_q12',
            canonicalOptions: [
                'No losses (0-10%)',
                'Small losses (11-20%)',
                'Moderate losses (21-49%)',
                'Large losses (50-90%)',
                'Total losses (91-100%)',
            ],
        },
    ];

/** The change of one risk question, by the id of version 3.0. */
export function clientApplicationRiskQuestionChange(
    questionId: string,
): ClientApplicationRiskQuestionChange | undefined {
    return CLIENT_APPLICATION_RISK_QUESTION_CHANGES.find(
        (change) => change.questionId === questionId,
    );
}

/** The v6 words of each canonical option of one instrument question, in order. */
export function clientApplicationRiskOptions(
    atrQuestionId: string,
    canonicalOptions: readonly string[],
): string[] {
    return canonicalOptions.map((label) =>
        atrOptionWording(EXISTING_CLIENT_ATR_WORDING, atrQuestionId, label),
    );
}

/**
 * The map from the v6 words to the canonical label, for each option whose
 * words differ. This is the map of SEP-288 read in the other direction.
 */
export function clientApplicationRiskOptionValues(
    atrQuestionId: string,
    canonicalOptions: readonly string[],
): Readonly<Record<string, string>> {
    const out: Record<string, string> = {};
    for (const label of canonicalOptions) {
        const words = atrOptionWording(EXISTING_CLIENT_ATR_WORDING, atrQuestionId, label);
        if (words !== label) out[words] = label;
    }
    return out;
}
